import { Pressable, StyleSheet, Text, View } from 'react-native';
import {
  openDateTimePicker,
  type DateTimePickEvent,
} from './dateTimePicker';
import { DAY_MS } from '@heraion/shared';
import { formatDayMonthYear } from './formatDate';
import { useNowTick } from '../index';
import type { useTheme } from './theme';

// Footer date editor (1.67.1) — sits below WorkoutTotalsCard in the
// Logger modal. Surfaces three native pickers (date / start time /
// end time) plus a derived Durata. When the user hasn't typed an end
// time yet (active workout) the duration ticks live every second so
// "quanto tempo è passato dall'inizio" stays current. Mirrors the web
// `.workout-date-card` from 1.67.0; both surfaces read identically on
// a synced household.
//
// 1.67.2: switched from the declarative `<DateTimePicker>` component
// to the imperative `DateTimePickerAndroid.open()` API. The
// declarative variant treats `value` as a controlled prop: any parent
// re-render (notably the 1-second live-tick driving the duration
// label) calls setNativeProps on the underlying Android dialog and
// resets the OS picker's internal state back to the original time. So
// the user would scroll from 8:47 to 8:23, the parent re-rendered on
// the next interval tick, and the dialog reverted to 8:47 before OK
// was pressed. The imperative API hands a Date snapshot to the OS
// dialog once at open time and never touches it again.
//
// RW-19: that imperative call is now reached through ./dateTimePicker, which
// splits per platform — the Android dialog on mobile, an <input> the browser
// opens on web-rnw. The card, and the arithmetic below it, stay one copy.

type Props = {
  startedAt: string;
  endedAt: string | undefined;
  onSetStartedAt: (iso: string) => void;
  onSetEndedAt: (iso: string | null) => void;
  locale: string;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

function formatDuration(totalSec: number): string | null {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return null;
  const s = Math.floor(totalSec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (s < 60) return `0:${String(s).padStart(2, '0')}`;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function formatTimeLabel(iso: string | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toTimeString().slice(0, 5);
  }
}

// Apply a hour/minute pick onto an existing ISO timestamp without
// changing the date portion. The community DateTimePicker returns a
// fresh Date object with today's date when in 'time' mode, so we
// stitch the picked clock back onto the workout's existing day.
function withTimeFrom(baseIso: string, picked: Date): string {
  const base = new Date(baseIso);
  if (Number.isNaN(base.getTime())) return picked.toISOString();
  base.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
  return base.toISOString();
}

// Apply a date pick onto an existing ISO timestamp, preserving the
// time-of-day. Used for the shared Date field so the user only has to
// pick the day once for both start and end.
function withDateFrom(baseIso: string, picked: Date): string {
  const base = new Date(baseIso);
  if (Number.isNaN(base.getTime())) {
    const copy = new Date(picked);
    copy.setHours(0, 0, 0, 0);
    return copy.toISOString();
  }
  const next = new Date(picked);
  next.setHours(base.getHours(), base.getMinutes(), base.getSeconds(), 0);
  return next.toISOString();
}

export function DateEditorCard({
  startedAt,
  endedAt,
  onSetStartedAt,
  onSetEndedAt,
  locale,
  theme,
  t,
}: Props) {
  // Live tick for the live-duration mode: only mount the interval
  // while endedAt is unset so we don't burn a setInterval after the
  // user has typed an end time (or hydrated one from a saved workout).
  // Shared with the Logger's own live tick (CN-11) — same
  // setInterval(1000) + cleanup, one primitive.
  const nowMs = useNowTick(!endedAt);

  const startMs = Date.parse(startedAt);
  const endMs = endedAt ? Date.parse(endedAt) : nowMs;
  const endIsLive = !endedAt;
  const durationSec =
    Number.isFinite(startMs) && Number.isFinite(endMs)
      ? Math.max(0, Math.round((endMs - startMs) / 1000))
      : null;
  const durationLabel = durationSec === null ? '—' : (formatDuration(durationSec) ?? '—');

  // Build a non-NaN seed for the OS dialog. Falls back to "now" when
  // the underlying ISO failed to parse (shouldn't happen during a
  // live workout, but the component opens defensively).
  const seedDate = (iso: string | undefined): Date => {
    if (iso) {
      const d = new Date(iso);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  };

  const handleDatePick = (event: DateTimePickEvent, picked?: Date) => {
    if (event.type !== 'set' || !picked) return;
    // Shift both start and end by the same day delta so multi-day
    // edits preserve the span. The Date object the picker returns is
    // the new day-of-month; we apply it to startedAt's date portion,
    // then mirror the shift onto endedAt if present.
    const newStart = withDateFrom(startedAt, picked);
    onSetStartedAt(newStart);
    if (endedAt) {
      const oldStartMs = Date.parse(startedAt);
      const oldEndMs = Date.parse(endedAt);
      const newStartMs = Date.parse(newStart);
      if (
        Number.isFinite(oldStartMs) &&
        Number.isFinite(oldEndMs) &&
        Number.isFinite(newStartMs)
      ) {
        const span = oldEndMs - oldStartMs;
        onSetEndedAt(new Date(newStartMs + span).toISOString());
      }
    }
  };

  const handleStartTimePick = (event: DateTimePickEvent, picked?: Date) => {
    if (event.type !== 'set' || !picked) return;
    onSetStartedAt(withTimeFrom(startedAt, picked));
  };

  const handleEndTimePick = (event: DateTimePickEvent, picked?: Date) => {
    if (event.type !== 'set' || !picked) return;
    // End time picks reuse the start's date as the base so the
    // workout stays on one day; if the typed end time is earlier
    // than the start, advance the end date by 1 day (cross-midnight).
    const base = endedAt ?? startedAt;
    let nextEnd = withTimeFrom(base, picked);
    const startBaseMs = Date.parse(startedAt);
    const endBaseMs = Date.parse(nextEnd);
    if (
      Number.isFinite(startBaseMs) &&
      Number.isFinite(endBaseMs) &&
      endBaseMs < startBaseMs
    ) {
      // An end before the start means the workout crossed midnight, so roll
      // the end onto the next day. This is a real day DURATION, which is why
      // it collapses onto DAY_MS (CN-31d(a)).
      const advanced = new Date(endBaseMs + DAY_MS);
      nextEnd = advanced.toISOString();
    }
    onSetEndedAt(nextEnd);
  };

  const openDatePicker = () =>
    openDateTimePicker({
      value: seedDate(startedAt),
      mode: 'date',
      onChange: handleDatePick,
    });

  const openStartTimePicker = () =>
    openDateTimePicker({
      value: seedDate(startedAt),
      mode: 'time',
      onChange: handleStartTimePick,
    });

  const openEndTimePicker = () =>
    openDateTimePicker({
      value: seedDate(endedAt ?? startedAt),
      mode: 'time',
      onChange: handleEndTimePick,
    });

  const styles = makeStyles(theme);

  return (
    <View style={styles.card}>
      <Text style={styles.cardLabel}>
        {t('mobile.logger.dateEditor', { defaultValue: 'Date & duration' })}
      </Text>
      <View style={styles.grid}>
        <Pressable
          accessibilityRole="button"
          onPress={openDatePicker}
          style={({ pressed }) => [styles.field, pressed && styles.fieldPressed]}
        >
          <Text style={styles.fieldLabel}>
            {t('mobile.logger.dateLabel', { defaultValue: 'Date' })}
          </Text>
          <Text style={styles.fieldValue}>{formatDayMonthYear(new Date(startedAt), locale)}</Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          onPress={openStartTimePicker}
          style={({ pressed }) => [styles.field, pressed && styles.fieldPressed]}
        >
          <Text style={styles.fieldLabel}>
            {t('mobile.logger.startTimeLabel', { defaultValue: 'Start' })}
          </Text>
          <Text style={styles.fieldValue}>{formatTimeLabel(startedAt, locale)}</Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          onPress={openEndTimePicker}
          style={({ pressed }) => [styles.field, pressed && styles.fieldPressed]}
        >
          <Text style={styles.fieldLabel}>
            {t('mobile.logger.endTimeLabel', { defaultValue: 'End' })}
          </Text>
          <Text style={styles.fieldValue}>{formatTimeLabel(endedAt, locale)}</Text>
        </Pressable>
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>
            {t('mobile.logger.durationLabel', { defaultValue: 'Duration' })}
          </Text>
          <View style={styles.durationRow}>
            <Text
              style={[
                styles.fieldValue,
                endIsLive && styles.durationLive,
              ]}
            >
              {durationLabel}
            </Text>
            {endIsLive ? (
              <Text style={[styles.dot, styles.dotLive]} aria-hidden>
                ●
              </Text>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    cardLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s.s3,
    },
    field: {
      minWidth: '45%',
      flexGrow: 1,
      gap: s.s1,
      paddingVertical: s.s1,
    },
    fieldPressed: {
      opacity: 0.55,
    },
    fieldLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    fieldValue: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontVariant: ['tabular-nums'],
    },
    durationRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    durationLive: {
      color: p.brand500,
    },
    dot: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
    },
    dotLive: {
      color: p.brand500,
    },
  });
}
